/**
 * KundenteamsPage — team-based customer visibility admin (Berechtigungskonzept V3.0,
 * Bereich 2, WS2).
 *
 * A SEPARATE axis from the task-routing "Teams" (Funktionsgruppen) page. Here an admin
 * creates restricted-visibility teams, manages their members, and assigns customers to them
 * (m:n) — a customer assigned to a team is visible ONLY to that team's members. The
 * non-deletable "Eigene Organisation" system team is shown but cannot be deleted/renamed; its
 * membership is managed only with the dedicated permission (the api enforces this and returns
 * 403 otherwise — surfaced inline).
 *
 * Reads → admin.team.read (nav gate). Mutations → admin.team.manage (canManage). The api is the
 * hard authorization line; canManage only shapes the UI.
 *
 * [Source: apps/web/src/features/administration/pages/TeamsPage.tsx] (structure + mutation pattern)
 */

import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AdminMemberDto } from '@brokly/shared';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { AdminPageHeader } from '../AdminPageHeader';
import { ADMIN_PAGE_META } from '../adminNav';
import { useAdminMembers, useKundenteams, useKundenteam } from '../hooks';
import { useEffectivePermissions } from '../useEffectivePermissions';
import { Button, Dialog, LoadingState, ErrorState, EmptyState, TextInput } from '../components';
import type { KundenteamDto, KundenteamCustomerDto } from '../types';

const meta = ADMIN_PAGE_META.kundenteams;

function memberLabel(m: AdminMemberDto): string {
  return m.display_name ?? m.email ?? m.user_id;
}

// ── Create-team dialog ─────────────────────────────────────────────────────────
function CreateTeamDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      apiClient.admin.createKundenteam({ name, description: description || null }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.adminKundenteams() });
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Neues Kundenteam"
      description="Ein Team, dem zugewiesene Kunden nur für dessen Mitglieder sichtbar sind."
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => create.mutate()} disabled={name.trim() === '' || create.isPending}>
            Erstellen
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block text-sm">
          <span className="mb-1 block text-muted-foreground">Name</span>
          <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="z. B. VIP-Mandate" />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block text-muted-foreground">Beschreibung (optional)</span>
          <TextInput value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        {error != null && <p className="text-sm text-destructive">{error}</p>}
      </div>
    </Dialog>
  );
}

// ── Team detail dialog (members + customers) ────────────────────────────────────
function TeamDetailDialog({
  team,
  members,
  canManage,
  canManageEigeneOrg,
  onClose,
}: {
  team: KundenteamDto;
  members: AdminMemberDto[];
  canManage: boolean;
  canManageEigeneOrg: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const detail = useKundenteam(team.id);
  // Concept 5.5 ("nicht Erlaubtes ist unsichtbar" — the affordance is REMOVED, not left to 403):
  // the system team's member/customer mutations need the dedicated
  // admin.team.eigene_organisation.verwalten (UC1) on top of admin.team.manage. Without it, the
  // api returns 403; so for the system team we HIDE the mutation affordances rather than let a
  // click fail. Restricted (non-system) teams are unaffected — admin.team.manage is enough.
  const canMutate = canManage && (!team.is_system_team || canManageEigeneOrg);
  const [error, setError] = useState<string | null>(null);
  const [memberToAdd, setMemberToAdd] = useState('');
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerHits, setCustomerHits] = useState<KundenteamCustomerDto[]>([]);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.adminKundenteam(team.id) });
    void qc.invalidateQueries({ queryKey: qk.adminKundenteams() });
  };

  const addMember = useMutation({
    mutationFn: (userId: string) => apiClient.admin.addKundenteamMember(team.id, userId),
    onSuccess: () => {
      setMemberToAdd('');
      setError(null);
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });
  const removeMember = useMutation({
    mutationFn: (userId: string) => apiClient.admin.removeKundenteamMember(team.id, userId),
    onSuccess: () => invalidate(),
    onError: (e: Error) => setError(e.message),
  });
  const assignCustomer = useMutation({
    mutationFn: (c: KundenteamCustomerDto) =>
      apiClient.admin.assignKundenteamCustomer(team.id, c.subject_type, c.subject_id),
    onSuccess: () => {
      setCustomerQuery('');
      setCustomerHits([]);
      setError(null);
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });
  const unassignCustomer = useMutation({
    mutationFn: (c: KundenteamCustomerDto) =>
      apiClient.admin.unassignKundenteamCustomer(team.id, c.subject_type, c.subject_id),
    onSuccess: () => invalidate(),
    onError: (e: Error) => setError(e.message),
  });
  const search = useMutation({
    mutationFn: (q: string) => apiClient.admin.searchKundenteamCustomers(q),
    onSuccess: (res) => setCustomerHits(res.items),
    onError: (e: Error) => setError(e.message),
  });

  const assignedMemberIds = new Set((detail.data?.members ?? []).map((m) => m.user_id));
  const candidateMembers = members.filter((m) => !assignedMemberIds.has(m.user_id));

  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={team.name}
      description={
        team.is_system_team
          ? 'Systemteam „Eigene Organisation": nicht löschbar. Mitgliedschaft benötigt eine gesonderte Berechtigung.'
          : (team.description ?? undefined)
      }
      footer={
        <Button variant="outline" onClick={onClose}>
          Schliessen
        </Button>
      }
    >
      {detail.isLoading ? (
        <LoadingState />
      ) : detail.isError ? (
        <ErrorState />
      ) : (
        <div className="space-y-6">
          {error != null && <p className="text-sm text-destructive">{error}</p>}

          {/* Members */}
          <section>
            <h3 className="mb-2 text-sm font-semibold">Mitglieder</h3>
            {(detail.data?.members ?? []).length === 0 ? (
              <EmptyState label="Noch keine Mitglieder." />
            ) : (
              <ul className="space-y-1">
                {(detail.data?.members ?? []).map((m) => (
                  <li
                    key={m.user_id}
                    className="flex items-center justify-between rounded-md border border-border px-3 py-1.5 text-sm"
                  >
                    <span className="truncate">{m.display_name ?? m.user_id}</span>
                    {canMutate && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeMember.mutate(m.user_id)}
                        disabled={removeMember.isPending}
                      >
                        Entfernen
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {canMutate && (
              <div className="mt-2 flex items-center gap-2">
                <select
                  className="h-9 flex-1 rounded-md border border-border bg-background px-2 text-sm"
                  value={memberToAdd}
                  onChange={(e) => setMemberToAdd(e.target.value)}
                >
                  <option value="">Mitglied auswählen …</option>
                  {candidateMembers.map((m) => (
                    <option key={m.user_id} value={m.user_id}>
                      {memberLabel(m)}
                    </option>
                  ))}
                </select>
                <Button
                  onClick={() => memberToAdd && addMember.mutate(memberToAdd)}
                  disabled={memberToAdd === '' || addMember.isPending}
                >
                  Hinzufügen
                </Button>
              </div>
            )}
          </section>

          {/* Customers */}
          <section>
            <h3 className="mb-2 text-sm font-semibold">Zugewiesene Kunden</h3>
            {(detail.data?.customers ?? []).length === 0 ? (
              <EmptyState label="Noch keine Kunden zugewiesen." />
            ) : (
              <ul className="space-y-1">
                {(detail.data?.customers ?? []).map((c) => (
                  <li
                    key={`${c.subject_type}:${c.subject_id}`}
                    className="flex items-center justify-between rounded-md border border-border px-3 py-1.5 text-sm"
                  >
                    <span className="truncate">
                      {c.name ?? c.subject_id}{' '}
                      <span className="text-xs text-muted-foreground">({c.subject_type})</span>
                    </span>
                    {canMutate && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => unassignCustomer.mutate(c)}
                        disabled={unassignCustomer.isPending}
                      >
                        Entfernen
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {canMutate && (
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-2">
                  <TextInput
                    value={customerQuery}
                    onChange={(e) => setCustomerQuery(e.target.value)}
                    placeholder="Kunde suchen …"
                  />
                  <Button
                    variant="outline"
                    onClick={() => customerQuery.trim() && search.mutate(customerQuery.trim())}
                    disabled={customerQuery.trim() === '' || search.isPending}
                  >
                    Suchen
                  </Button>
                </div>
                {customerHits.length > 0 && (
                  <ul className="space-y-1 rounded-md border border-border p-2">
                    {customerHits.map((c) => (
                      <li
                        key={`${c.subject_type}:${c.subject_id}`}
                        className="flex items-center justify-between px-1 py-1 text-sm"
                      >
                        <span className="truncate">
                          {c.name ?? c.subject_id}{' '}
                          <span className="text-xs text-muted-foreground">({c.subject_type})</span>
                        </span>
                        <Button size="sm" onClick={() => assignCustomer.mutate(c)} disabled={assignCustomer.isPending}>
                          Zuweisen
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </section>
        </div>
      )}
    </Dialog>
  );
}

// ── One team row ───────────────────────────────────────────────────────────────
function TeamRow({
  team,
  canManage,
  onOpen,
  onDelete,
}: {
  team: KundenteamDto;
  canManage: boolean;
  onOpen: () => void;
  onDelete: () => void;
}) {
  return (
    <li className="flex items-center gap-3 rounded-lg border border-border px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold">{team.name}</span>
          {team.is_system_team && (
            <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">
              Systemteam
            </span>
          )}
        </div>
        {team.description != null && (
          <div className="truncate text-xs text-muted-foreground">{team.description}</div>
        )}
      </div>
      <div className="shrink-0 text-xs text-muted-foreground">
        {team.member_count ?? 0} Mitglieder · {team.customer_count ?? 0} Kunden
      </div>
      <Button variant="outline" size="sm" onClick={onOpen}>
        {canManage ? 'Verwalten' : 'Ansehen'}
      </Button>
      {canManage && !team.is_system_team && (
        <Button variant="outline" size="sm" onClick={onDelete}>
          Löschen
        </Button>
      )}
    </li>
  );
}

export function KundenteamsPage() {
  const { can } = useEffectivePermissions();
  const canManage = can('admin.team.manage');
  // UC1: system-team membership + its customer scope need the dedicated permission on top of
  // admin.team.manage. Used to hide (not 403) the system team's mutation affordances (5.5).
  const canManageEigeneOrg = can('admin.team.eigene_organisation.verwalten');
  const teamsQuery = useKundenteams();
  const membersQuery = useAdminMembers();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [detailTeam, setDetailTeam] = useState<KundenteamDto | null>(null);

  const teams = useMemo(() => teamsQuery.data?.teams ?? [], [teamsQuery.data]);

  const deleteTeam = useMutation({
    mutationFn: (id: string) => apiClient.admin.deleteKundenteam(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.adminKundenteams() }),
  });

  return (
    <div>
      <AdminPageHeader
        title={meta.title}
        description={meta.description}
        actions={canManage ? <Button onClick={() => setCreating(true)}>Neues Team</Button> : undefined}
      />
      {teamsQuery.isLoading ? (
        <LoadingState />
      ) : teamsQuery.isError ? (
        <ErrorState />
      ) : teams.length === 0 ? (
        <EmptyState label="Noch keine Kundenteams. Standard: alle sehen alle Kunden ausser die eigene Organisation." />
      ) : (
        <ul className="space-y-2">
          {teams.map((t) => (
            <TeamRow
              key={t.id}
              team={t}
              canManage={canManage}
              onOpen={() => setDetailTeam(t)}
              onDelete={() => {
                if (window.confirm(`Team „${t.name}" löschen?`)) deleteTeam.mutate(t.id);
              }}
            />
          ))}
        </ul>
      )}
      {creating && <CreateTeamDialog onClose={() => setCreating(false)} />}
      {detailTeam != null && (
        <TeamDetailDialog
          team={detailTeam}
          members={membersQuery.data?.members ?? []}
          canManage={canManage}
          canManageEigeneOrg={canManageEigeneOrg}
          onClose={() => setDetailTeam(null)}
        />
      )}
    </div>
  );
}
